/**
 * Yup schemas for Add/Edit User (AddUser page) — Radiologist & Technician (user story).
 */

import * as Yup from 'yup';
import { USER_MSG, validateUserDobStory, validateUserPasswordStory } from './userFormFieldRules';
import { isWellFormedEmailAddress } from './emailValidation';

const EMPLOYEE_ID_RE = /^[A-Za-z0-9]+$/;
const FULL_NAME_RE = /^[A-Za-z ]+$/;
const PHONE_IN_RE = /^[6-9][0-9]{9}$/;
const USERNAME_RE = /^[a-z0-9._]+$/;

function buildAddUserSchema(hasRadiologistHierarchy?: boolean) {
  return Yup.object().shape({
    role: Yup.string()
      .trim()
      .required(USER_MSG.role)
      .oneOf(['Radiologist', 'Technician'], USER_MSG.role),

    employeeId: Yup.string()
      .trim()
      .required(USER_MSG.employeeId)
      .min(3, USER_MSG.employeeId)
      .max(20, USER_MSG.employeeId)
      .matches(EMPLOYEE_ID_RE, USER_MSG.employeeId),

    fullName: Yup.string()
      .transform(v => String(v ?? '').trim())
      .required(USER_MSG.fullName)
      .min(3, USER_MSG.fullName)
      .max(100, USER_MSG.fullName)
      .matches(FULL_NAME_RE, USER_MSG.fullName),

    gender: Yup.string()
      .trim()
      .required(USER_MSG.gender)
      .oneOf(['Male', 'Female', 'Other'], USER_MSG.gender),

    dateOfBirth: Yup.string()
      .trim()
      .required(USER_MSG.dob)
      .test('dob-age', USER_MSG.dob, v => validateUserDobStory(v) === undefined),

    email: Yup.string()
      .trim()
      .required(USER_MSG.email)
      .test('email-general', USER_MSG.email, v => isWellFormedEmailAddress(String(v ?? '').trim())),

    phoneNo: Yup.string()
      .transform(v =>
        String(v ?? '')
          .replace(/\D/g, '')
          .slice(-10)
      )
      .required(USER_MSG.phone)
      .matches(PHONE_IN_RE, USER_MSG.phone),

    username: Yup.string()
      .trim()
      .required(USER_MSG.username)
      .min(5, USER_MSG.username)
      .max(30, USER_MSG.username)
      .matches(USERNAME_RE, USER_MSG.username),

    password: Yup.string().test('password', function (value) {
      if (this.parent.isEdit === true) {
        return true;
      }
      if (!value || value === '••••••') {
        return this.createError({ message: USER_MSG.password });
      }
      const err = validateUserPasswordStory(value, this.parent.username, this.parent.email);
      if (err) {
        return this.createError({ message: err });
      }
      return true;
    }),

    status: Yup.string()
      .trim()
      .required(USER_MSG.status)
      .oneOf(['active', 'inactive'], USER_MSG.status),

    isEdit: Yup.boolean().notRequired(),

    sub_role: Yup.string()
      .trim()
      .test('sub-role', 'Sub Role is required', function (v) {
        const role = this.parent.role;
        if (role !== 'Radiologist') {
          return true;
        }
        return Boolean(v && ['Junior', 'Senior'].includes(String(v).trim()));
      }),

    shiftStart: Yup.string()
      .trim()
      .test('shift-start', 'Shift start time is required', function (v) {
        const r = this.parent.role;
        if (r !== 'Technician' && r !== 'Radiologist') {
          return true;
        }
        return Boolean(String(v ?? '').trim());
      }),

    shiftEnd: Yup.string()
      .trim()
      .test('shift-end', 'Shift end time is required', function (v) {
        const r = this.parent.role;
        if (r !== 'Technician' && r !== 'Radiologist') {
          return true;
        }
        return Boolean(String(v ?? '').trim());
      }),

    registrationNo: Yup.string()
      .trim()
      .test('reg-no', 'Registration No. is required', function (v) {
        if (this.parent.role !== 'Radiologist') {
          return true;
        }
        return Boolean(String(v ?? '').trim());
      }),

    qualification: Yup.string()
      .trim()
      .test('qual', 'Qualification is required', function (v) {
        if (this.parent.role !== 'Radiologist') {
          return true;
        }
        return Boolean(String(v ?? '').trim());
      }),

    reportingModalityAccess: Yup.array()
      .of(Yup.string())
      .test('modalities', 'At least one modality is required', function (v) {
        if (this.parent.role !== 'Radiologist') {
          return true;
        }
        return Array.isArray(v) && v.length > 0;
      }),

    experienceYears: Yup.number()
      .nullable()
      .transform((_value, originalValue) => {
        if (originalValue === '' || originalValue === null || originalValue === undefined) {
          return undefined;
        }
        const n = Number(originalValue);
        return Number.isFinite(n) ? n : Number.NaN;
      })
      .integer(USER_MSG.experienceYears)
      .min(0, USER_MSG.experienceYears)
      .max(99, USER_MSG.experienceYears)
      .optional(),

    experience: Yup.string()
      .trim()
      .test('exp-years-str', USER_MSG.experienceYears, v => !v || /^\d+$/.test(v)),
    specialty: Yup.string().trim().notRequired(),
    designation: Yup.string().trim().notRequired(),
    modality: Yup.array().of(Yup.string()).notRequired(),
    signature: Yup.mixed().notRequired(),
    signatureFileName: Yup.string().notRequired(),
    availabilityHours: Yup.array().of(Yup.string()).notRequired(),
    availabilityDays: Yup.array().of(Yup.string()).notRequired(),
  });
}

/**
 * Formik schema for AddUser. `role` argument kept for call-site compatibility; schema is unified with `.when('role', …)`.
 */
export function getUserValidationSchema(_role: string, hasRadiologistHierarchy?: boolean) {
  return buildAddUserSchema(hasRadiologistHierarchy);
}
